import { useQuery } from '@tanstack/react-query';
import type { PropsWithChildren, ReactElement } from 'react';
import { Navigate } from 'react-router-dom';

import { useAuth } from '../auth/use-auth';
import { AppLoading } from '../marketing/app-loading';
import { adminKeys, fetchWhoami } from './framework/admin-api';

/**
 * The client-side gate.
 *
 * **This is not the security boundary** — `AdminGuard` on the API is, and it
 * refuses every `/admin/*` route regardless of what this component decides.
 * What this buys is that a non-admin who finds the URL gets sent home instead of
 * a screen full of failed requests.
 *
 * It asks the server rather than checking the address locally: the domain rule
 * and the `ADMIN_EMAILS` allowlist live in one place, and a client-side copy is
 * one that can drift.
 */
export function AdminRoute({ children }: PropsWithChildren): ReactElement {
  const { loading, user } = useAuth();
  const { data, isPending } = useQuery({
    enabled: !!user,
    queryFn: fetchWhoami,
    queryKey: adminKeys.whoami(),
    // The answer only changes when the account does, and `useAuth` already
    // invalidates on sign-in and sign-out.
    staleTime: Infinity,
  });

  if (loading || (user && isPending)) {
    return <AppLoading label="Checking access…" />;
  }

  if (!user) return <Navigate replace to="/login" />;
  if (!data?.isAdmin) return <Navigate replace to="/" />;

  return <>{children}</>;
}

/** Whether to show the way in. Same question, without the redirect — used by
 *  the product's own user menu. */
export function useIsAdmin(): boolean {
  const { user } = useAuth();
  const { data } = useQuery({
    enabled: !!user,
    queryFn: fetchWhoami,
    queryKey: adminKeys.whoami(),
    staleTime: Infinity,
  });

  return data?.isAdmin === true;
}
